import React, { useState } from 'react';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Ship, Anchor, Bell, FileText, Zap, Package, Sparkles, Users, ShieldCheck, FileBarChart, Eye, Activity, History as HistoryIcon } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function buildSteps(role, isSuperAdmin) {
  const welcome = { icon: Sparkles, titleKey: 'onboarding.welcomeTitle', bodyKey: 'onboarding.welcomeBody' };

  // Super Admin (OFIMEX staff) — focuses on multi-tenant + diagnostics
  if (isSuperAdmin) {
    return [
      welcome,
      { icon: Eye, titleKey: 'onboarding.superMultiTenantTitle', bodyKey: 'onboarding.superMultiTenantBody' },
      { icon: Users, titleKey: 'onboarding.superViewAsTitle', bodyKey: 'onboarding.superViewAsBody' },
      { icon: Activity, titleKey: 'onboarding.superDiagnosticsTitle', bodyKey: 'onboarding.superDiagnosticsBody' },
    ];
  }

  // Client — short tour, 3 steps after welcome (already existed)
  if (role === 'client') {
    return [
      welcome,
      { icon: Ship, titleKey: 'onboarding.clientTrackingTitle', bodyKey: 'onboarding.clientTrackingBody' },
      { icon: FileText, titleKey: 'onboarding.clientDocsTitle', bodyKey: 'onboarding.clientDocsBody' },
      { icon: Bell, titleKey: 'onboarding.clientNotifTitle', bodyKey: 'onboarding.clientNotifBody' },
    ];
  }

  // Admin — broader tour: ops basics + admin features
  if (role === 'admin') {
    return [
      welcome,
      { icon: Anchor, titleKey: 'onboarding.opsTrackingTitle', bodyKey: 'onboarding.opsTrackingBody' },
      { icon: Ship, titleKey: 'onboarding.opsCreateTitle', bodyKey: 'onboarding.opsCreateBody' },
      { icon: Users, titleKey: 'onboarding.adminUsersTitle', bodyKey: 'onboarding.adminUsersBody' },
      { icon: ShieldCheck, titleKey: 'onboarding.adminTrackingHealthTitle', bodyKey: 'onboarding.adminTrackingHealthBody' },
      { icon: HistoryIcon, titleKey: 'onboarding.adminAuditTitle', bodyKey: 'onboarding.adminAuditBody' },
      { icon: FileBarChart, titleKey: 'onboarding.adminReportsTitle', bodyKey: 'onboarding.adminReportsBody' },
    ];
  }

  // Operator (default) — focused on day-to-day workflow
  return [
    welcome,
    { icon: Anchor, titleKey: 'onboarding.opsTrackingTitle', bodyKey: 'onboarding.opsTrackingBody' },
    { icon: Ship, titleKey: 'onboarding.opsCreateTitle', bodyKey: 'onboarding.opsCreateBody' },
    { icon: FileText, titleKey: 'onboarding.opsDocsTitle', bodyKey: 'onboarding.opsDocsBody' },
    { icon: Package, titleKey: 'onboarding.opsDeliveryTitle', bodyKey: 'onboarding.opsDeliveryBody' },
    { icon: Bell, titleKey: 'onboarding.opsAlertsTitle', bodyKey: 'onboarding.opsAlertsBody' },
  ];
}

export function OnboardingWizard() {
  const { t } = useI18n();
  const { user, loading, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [forced, setForced] = useState(false);
  const [step, setStep] = useState(0);

  // Open the wizard automatically only after backend confirms onboarding is pending
  React.useEffect(() => {
    if (!loading && user && user.onboarding_completed === false) {
      setOpen(true);
    }
  }, [loading, user]);

  // Allow opening the wizard on demand from anywhere (e.g. a "View tour" button)
  React.useEffect(() => {
    const handler = () => {
      setStep(0);
      setForced(true);
      setOpen(true);
    };
    window.addEventListener('ofimex:open-tour', handler);
    return () => window.removeEventListener('ofimex:open-tour', handler);
  }, []);

  // Wait until auth has hydrated user from backend
  if (loading || !user) return null;
  // Show wizard if: it's a first-login pending case OR the user explicitly forced it
  const shouldShow = forced || user.onboarding_completed === false;
  if (!shouldShow) return null;
  if (!open) return null;

  const steps = buildSteps(user.role, !!user.is_super_admin);
  const total = steps.length;
  const current = steps[step];
  const Icon = current.icon;
  const isLast = step === total - 1;

  const persist = async () => {
    // Only persist on the first-time flow; manual replays should not toggle the flag again
    if (forced) return;
    try {
      const token = localStorage.getItem('ofimex_token');
      await axios.patch(`${API}/api/auth/me/onboarding`, { completed: true }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (refreshUser) await refreshUser();
    } catch {
      /* ignore — non-critical */
    }
  };

  const closeAll = () => {
    setOpen(false);
    setForced(false);
  };

  const handleSkip = async () => {
    closeAll();
    await persist();
  };

  const handleFinish = async () => {
    closeAll();
    await persist();
  };

  const handleNext = () => {
    if (isLast) handleFinish();
    else setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* lock — must use buttons */ }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="onboarding-wizard"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mx-auto mb-4">
            <Icon size={28} />
          </div>
          <DialogTitle className="text-center text-xl" data-testid="onboarding-title">
            {t(current.titleKey)}
          </DialogTitle>
          <DialogDescription className="text-center text-sm leading-relaxed pt-2" data-testid="onboarding-body">
            {t(current.bodyKey)}
          </DialogDescription>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 py-3">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'bg-primary w-8' : i < step ? 'bg-primary/50 w-1.5' : 'bg-muted w-1.5'
              }`}
            />
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground -mt-2">
          {t('onboarding.stepOf', { current: step + 1, total })}
        </p>

        <div className="flex items-center justify-between gap-2 pt-4">
          <Button
            variant="ghost"
            onClick={handleSkip}
            data-testid="onboarding-skip-btn"
            className="text-muted-foreground"
          >
            {t('onboarding.skip')}
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                onClick={handleBack}
                data-testid="onboarding-back-btn"
              >
                {t('onboarding.back')}
              </Button>
            )}
            <Button
              onClick={handleNext}
              data-testid="onboarding-next-btn"
            >
              {isLast ? t('onboarding.finish') : t('onboarding.next')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
